import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useRightRail } from "@/context/RightRailContext";
import { useKaiChat } from "@/features/kai/KaiChatContext";
import { buildKaiFeedEventContext } from "@/features/kai/kaiFeedEventContext";
import { isMobileShellViewport } from "@/lib/viewport";
import type { FeedEvent } from "@/types/feed";

export interface AskKaiOptions {
  /**
   * Keep the user on the current page on mobile instead of navigating to the
   * full-screen Kai route. Kai opens in the mobile workspace sheet, which lets
   * page-scoped live previews (e.g. the Home layout editor) keep working.
   * Ignored when `feedEvent` is set — those asks always open `/`.
   */
  stayOnPage?: boolean;
  /** Start a fresh chat before sending, discarding any active conversation. */
  newChat?: boolean;
  /**
   * A feed event to hand Kai as scoped context. Implies `newChat`: the prompt
   * lands in a clean thread with the event attached, so follow-ups keep it in
   * scope without the user restating the event.
   */
  feedEvent?: FeedEvent;
}

/**
 * Open Kai (right rail on desktop, `/` on mobile) and send it a prompt. Used by
 * the reports pages to hand a dashboard-build/edit request to the assistant,
 * and by feed rows to explain a specific event in a fresh chat.
 */
export function useAskKai() {
  const navigate = useNavigate();
  const rail = useRightRail();
  const kai = useKaiChat();

  return useCallback(
    (prompt: string, options?: AskKaiOptions) => {
      const feedEvent = options?.feedEvent;
      // A feed-event ask always starts clean so the event owns the thread.
      if (options?.newChat || feedEvent) kai?.startNewChat();
      if (feedEvent) {
        kai?.setFeedEventFocus(buildKaiFeedEventContext(feedEvent));
      }

      if (isMobileShellViewport()) {
        // Feed-event asks always go to the full Kai page. `stayOnPage` is only
        // for in-place flows (e.g. Home layout edit) that need a live preview.
        if (options?.stayOnPage && !feedEvent) {
          rail.openMobileKai();
        } else {
          navigate("/");
        }
      } else if (!rail.kaiPanelActive) {
        rail.toggleKaiPanel();
      }
      kai?.sendMessage(prompt);
    },
    [navigate, rail, kai],
  );
}
